import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";

import "./Home.css";

/* =========================================================
   IMAGE IMPORTS
========================================================= */
import slider1 from "../../src/assets/School.jpg";
import slider2 from "../../src/assets/Teacher2.jpg";
import slider3 from "../../src/assets/Teacher3.jpg";

const safeSrc = (img) =>
  typeof img === "string" && img.length > 0 ? img : null;

/* Safe localStorage helpers (private mode / blocked storage me crash nahi hoga) */
const store = {
  get: (k, fallback = null) => {
    try {
      const v = localStorage.getItem(k);
      return v === null ? fallback : v;
    } catch {
      return fallback;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* ignore */
    }
  },
  remove: (k) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  },
};

/* "Platform Statistics" → ["Platform", "Statistics"] (Hindi me bhi chalega) */
const splitTitle = (s = "") => {
  const w = s.trim().split(/\s+/);
  return w.length < 2 ? ["", s] : [w.slice(0, -1).join(" "), w[w.length - 1]];
};

/* =========================================================
   TOAST SYSTEM
========================================================= */
let toastId = 0;
let toastListeners = [];

const emitToast = (msg, type = "info") => {
  const toast = { id: ++toastId, msg, type };
  toastListeners.forEach((fn) => fn(toast));
  setTimeout(() => {
    toastListeners.forEach((fn) => fn({ ...toast, dismiss: true }));
  }, 4000);
};

const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    const handler = (toast) =>
      setToasts((prev) =>
        toast.dismiss
          ? prev.filter((x) => x.id !== toast.id)
          : [...prev, toast]
      );
    toastListeners.push(handler);
    return () => {
      toastListeners = toastListeners.filter((f) => f !== handler);
    };
  }, []);
  return toasts;
};

/* =========================================================
   TRANSLATIONS
========================================================= */
const T = {
  en: {
    brand: "Student School Darpan",
    tagline: "Empowering Education Across Rajasthan",
    searchPlaceholder: "Please enter valid school code",
    search: "Search",
    categories: "School Search Categories",
    categoriesSub:
      "Explore powerful search categories designed to help you quickly discover the right school information.",
    findSchool: "Find Your School",
    explore: "Explore Categories",
    stats: "Platform Statistics",
    schemes: "Government Schemes",
    notices: "Latest Notices",
    faq: "Frequently Asked Questions",
    testimonials: "What Users Say",
    footerDesc:
      "A modern digital platform to discover schools, educational services, schemes and important school information across Rajasthan.",
    quickLinks: "Quick Links",
    resources: "Resources",
    contact: "Contact",
    newsletter: "Subscribe to Newsletter",
    newsletterDesc: "Get updates about new schools and schemes",
    emailPlaceholder: "Your email address",
    subscribe: "Subscribe",
    home: "Home",
    searchSchools: "Search Schools",
    reports: "Reports",
    about: "About Us",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    sitemap: "Sitemap",
    help: "Help Center",
    rights: "All rights reserved.",
    quickAccess: "Quick access:",
    recent: "Recent:",
    clear: "Clear",
    ctaTitle: "Ready to find the right school?",
    readFaq: "Read FAQs",
  },
  hi: {
    brand: "स्टूडेंट स्कूल दर्पण",
    tagline: "राजस्थान भर में शिक्षा को सशक्त बनाना",
    searchPlaceholder: "कृपया वैध स्कूल कोड दर्ज करें",
    search: "खोजें",
    categories: "स्कूल खोज श्रेणियाँ",
    categoriesSub:
      "सही स्कूल जानकारी तेजी से खोजने के लिए डिज़ाइन की गई शक्तिशाली खोज श्रेणियाँ।",
    findSchool: "अपना स्कूल खोजें",
    explore: "श्रेणियाँ देखें",
    stats: "प्लेटफ़ॉर्म आंकड़े",
    schemes: "सरकारी योजनाएँ",
    notices: "नवीनतम सूचनाएँ",
    faq: "अक्सर पूछे जाने वाले प्रश्न",
    testimonials: "उपयोगकर्ता क्या कहते हैं",
    footerDesc:
      "राजस्थान भर में स्कूलों, शैक्षिक सेवाओं, योजनाओं और महत्वपूर्ण स्कूल जानकारी खोजने के लिए आधुनिक डिजिटल प्लेटफ़ॉर्म।",
    quickLinks: "त्वरित लिंक",
    resources: "संसाधन",
    contact: "संपर्क",
    newsletter: "न्यूज़लेटर की सदस्यता लें",
    newsletterDesc: "नए स्कूलों और योजनाओं के बारे में अपडेट पाएं",
    emailPlaceholder: "आपका ईमेल पता",
    subscribe: "सदस्यता लें",
    home: "होम",
    searchSchools: "स्कूल खोजें",
    reports: "रिपोर्ट",
    about: "हमारे बारे में",
    privacy: "गोपनीयता नीति",
    terms: "सेवा की शर्तें",
    sitemap: "साइटमैप",
    help: "सहायता केंद्र",
    rights: "सर्वाधिकार सुरक्षित।",
    quickAccess: "त्वरित पहुँच:",
    recent: "हाल की खोज:",
    clear: "हटाएँ",
    ctaTitle: "सही स्कूल खोजने के लिए तैयार हैं?",
    readFaq: "FAQ पढ़ें",
  },
};

const useLang = () => {
  const [lang, setLang] = useState(() => {
    const saved = store.get("ssd-lang", "en");
    return T[saved] ? saved : "en";
  });
  const change = (l) => {
    setLang(l);
    store.set("ssd-lang", l);
  };
  return { lang, t: T[lang], change };
};

/* =========================================================
   HERO SLIDES
========================================================= */
const sliders = [
  {
    src: safeSrc(slider1),
    fallback: "STUDENT SCHOOL DARPAN",
    eyebrow: "STUDENT SCHOOL DARPAN",
    title: "Empowering Education",
    highlight: "Across Rajasthan",
    subtitle:
      "A modern digital platform to discover schools, educational services, schemes and important school information.",
  },
  {
    src: safeSrc(slider2),
    fallback: "FIND YOUR SCHOOL",
    eyebrow: "SMART SCHOOL SEARCH",
    title: "Find Your",
    highlight: "School Faster",
    subtitle:
      "Search schools using DISE, NIC Code, Pincode, District and multiple school categories.",
  },
  {
    src: safeSrc(slider3),
    fallback: "EXPLORE OPPORTUNITIES",
    eyebrow: "EDUCATION OPPORTUNITIES",
    title: "Explore Schools &",
    highlight: "Education Programs",
    subtitle:
      "Discover school categories, streams, vocational trades, facilities and educational opportunities.",
  },
];

/* =========================================================
   WEBFORM HELPERS (aapke ASP.NET backend ke liye same rakhe hain)
========================================================= */
const doPostBack = (target, arg) => {
  if (typeof window !== "undefined" && window.__doPostBack) {
    window.__doPostBack(target, arg);
  } else {
    console.log("PostBack:", target, arg);
  }
};

const WebForm_PostBackOptions = (
  eventTarget,
  eventArgument,
  validation,
  validationGroup,
  actionUrl,
  trackFocus,
  clientSubmit
) => ({
  eventTarget,
  eventArgument,
  validation,
  validationGroup,
  actionUrl,
  trackFocus,
  clientSubmit,
});

const WebForm_DoPostBackWithOptions = (options) => {
  console.log("WebForm_DoPostBackWithOptions:", options);
};

const ValidateCaptcha = (...args) => {
  console.log("ValidateCaptcha:", args);
  return true;
};

const showpopup = (n) => {
  console.log("showpopup:", n);
};

const P = (name) => `ctl00$ContentPlaceHolder1$${name}`;

/* =========================================================
   INTERACTION HELPERS
========================================================= */
const handleSpotlight = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty(
    "--mx",
    `${((e.clientX - rect.left) / rect.width) * 100}%`
  );
  e.currentTarget.style.setProperty(
    "--my",
    `${((e.clientY - rect.top) / rect.height) * 100}%`
  );
};

const handleCardTilt = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const px = (e.clientX - rect.left) / rect.width - 0.5;
  const py = (e.clientY - rect.top) / rect.height - 0.5;
  card.style.setProperty("--ry", `${px * 10}deg`);
  card.style.setProperty("--rx", `${py * -10}deg`);
};

const resetCardTilt = (e) => {
  e.currentTarget.style.setProperty("--ry", "0deg");
  e.currentTarget.style.setProperty("--rx", "0deg");
};

const handleMagnetic = (e) => {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  el.style.setProperty(
    "--tx",
    `${(e.clientX - rect.left - rect.width / 2) * 0.2}px`
  );
  el.style.setProperty(
    "--ty",
    `${(e.clientY - rect.top - rect.height / 2) * 0.2}px`
  );
};

const resetMagnetic = (e) => {
  e.currentTarget.style.setProperty("--tx", "0px");
  e.currentTarget.style.setProperty("--ty", "0px");
};

const spawnRipple = (e) => {
  const btn = e.currentTarget;
  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height);
  const ripple = document.createElement("span");
  ripple.className = "ssd-ripple";
  ripple.style.width = ripple.style.height = `${size}px`;
  ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
  ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
  btn.appendChild(ripple);
  ripple.addEventListener("animationend", () => ripple.remove());
};

const withRipple = (fn) => (e) => {
  spawnRipple(e);
  if (fn) fn(e);
};

const scrollToId = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

/* =========================================================
   DISTRICTS + BLOCKS
========================================================= */
const DISTRICTS = [
  ["0821", "AJMER"], ["0806", "ALWAR"], ["0835", "BALOTARA"], ["0828", "BANSWARA"],
  ["0831", "BARAN"], ["0817", "BARMER"], ["0836", "BEAWAR"], ["0807", "BHARATPUR"],
  ["0824", "BHILWARA"], ["0803", "BIKANER"], ["0823", "BUNDI"], ["0829", "CHITTAURGARH"],
  ["0804", "CHURU"], ["0811", "DAUSA"], ["0837", "DEEG"], ["0808", "DHAULPUR"],
  ["0838", "DIDWANA-KUCHAMAN"], ["0827", "DUNGARPUR"], ["0801", "GANGANAGAR"],
  ["0802", "HANUMANGARH"], ["0812", "JAIPUR"], ["0816", "JAISALMER"], ["0818", "JALOR"],
  ["0832", "JHALAWAR"], ["0805", "JHUNJHUNUN"], ["0815", "JODHPUR"], ["0809", "KARAULI"],
  ["0844", "KHAIRTHAL-TIJARA"], ["0830", "KOTA"], ["0845", "KOTPUTLI-BEHROR"],
  ["0814", "NAGAUR"], ["0820", "PALI"], ["0847", "PHALODI"], ["0833", "PRATAPGARH"],
  ["0825", "RAJSAMAND"], ["0810", "S.MADHOPUR"], ["0848", "SALUMBAR"], ["0813", "SIKAR"],
  ["0819", "SIROHI"], ["0822", "TONK"], ["0826", "UDAIPUR"],
];

const BLOCKS_BY_DISTRICT = {
  "0812": [
    ["1", "AMBER"], ["2", "BASSI"], ["3", "CHAKSU"], ["4", "CHOMU"], ["5", "DUDU"],
    ["6", "GOVINDGARH"], ["7", "JAIPUR"], ["8", "JAMWA RAMGARH"], ["9", "JHOTWARA"],
    ["10", "KOTPUTLI"], ["11", "MADHORAJ"], ["12", "MAUZAMABAD"], ["13", "PAOTA"],
    ["14", "PHAGI"], ["15", "SAMBAR"], ["16", "SANGANER"], ["17", "SHAHPURA"],
    ["18", "VIRATNAGAR"],
  ],
  "0821": [
    ["1", "AJMER"], ["2", "ARAI"], ["3", "BHINAY"], ["4", "KEKRI"], ["5", "KISHANGARH"],
    ["6", "MASUDA"], ["7", "PISANGAN"], ["8", "PUSHKAR"], ["9", "SAWAR"],
    ["10", "SHREE NAGAR"], ["11", "TATAGARH"],
  ],
};

/* =========================================================
   PAGE DATA  (har item ka apna color: c)
   NOTE: stats / notices / testimonials sample data hain —
   live launch se pehle real data se replace karein.
========================================================= */
const STATS = [
  { value: 125000, suffix: "+", label: "Schools Registered", icon: "🏫", c: "#4f46e5" },
  { value: DISTRICTS.length, suffix: "", label: "Districts Covered", icon: "🗺️", c: "#0ea5e9" },
  { value: 8500000, suffix: "+", label: "Students Impacted", icon: "🎓", c: "#ec4899" },
  { value: 65, suffix: "+", label: "Government Schemes", icon: "📜", c: "#f59e0b" },
];

const SCHEMES = [
  { title: "Samagra Shiksha Abhiyan", desc: "Comprehensive education scheme covering pre-school to class 12.", tag: "Active", color: "#2563eb", icon: "📚" },
  { title: "Mid-Day Meal Program", desc: "Nutritious meals for students in government and aided schools.", tag: "Active", color: "#10b981", icon: "🍛" },
  { title: "RTE Admission 2025", desc: "Right to Education free admission for economically weaker sections.", tag: "New", color: "#f59e0b", icon: "🎯" },
  { title: "Kasturba Gandhi Balika Vidyalaya", desc: "Residential schools for girls from disadvantaged groups.", tag: "Active", color: "#7c3aed", icon: "👩‍🎓" },
  { title: "PM e-Vidya Digital Learning", desc: "Online education platform for students in remote areas.", tag: "Active", color: "#06b6d4", icon: "💻" },
  { title: "Sanskrit Education Promotion", desc: "Special grants for Sanskrit schools and Vedic education.", tag: "Featured", color: "#f97316", icon: "🕉️" },
];

const FAQS = [
  { q: "How do I search for a school by DISE code?", a: "Use the global search bar at the top, enter the valid school DISE code and click Search. The system will display complete school information including address, contact and facilities." },
  { q: "Can I search schools by pincode?", a: "Yes. In any search category, select 'By Pincode' option and enter the 6-digit pincode to view all schools in that area." },
  { q: "What information is available for each school?", a: "School details include DISE code, NIC code, address, contact, streams offered, vocational trades, hostel facilities, computer lab availability and more." },
  { q: "Is this platform free to use?", a: "Yes, Student School Darpan is completely free for all students, parents and citizens of Rajasthan." },
  { q: "How often is the school data updated?", a: "The school database is updated regularly from official sources to ensure accuracy and completeness." },
  { q: "Can I search vocational trade schools?", a: "Yes. Use the 'Vocational Trade in Schools' category to filter schools by specific trades like IT, Healthcare, Automobile, etc." },
];

const TESTIMONIALS = [
  { name: "Priya Sharma", role: "Parent, Jaipur", text: "This platform made it incredibly easy to find the right school for my daughter. The district-wise search is a lifesaver!", avatar: "PS" },
  { name: "Ramesh Kumar", role: "Student, Jodhpur", text: "I found the perfect vocational trade school near my home using the pincode search. Highly recommended!", avatar: "RK" },
  { name: "Dr. Anjali Verma", role: "Education Officer", text: "A well-designed government platform. The data is comprehensive and the search filters are very helpful.", avatar: "AV" },
  { name: "Mohammed Irfan", role: "Teacher, Kota", text: "I use this to guide my students and their parents. The scheme information is especially useful.", avatar: "MI" },
];

const NOTICES = [
  { date: "15 Dec", text: "RTE Admission 2025 applications now open" },
  { date: "12 Dec", text: "New vocational trades added to 45 schools" },
  { date: "10 Dec", text: "Winter break schedule announced for all districts" },
  { date: "08 Dec", text: "Model School entrance exam results declared" },
  { date: "05 Dec", text: "Digital learning tablets distribution drive begins" },
];

const STEPS = [
  { n: "01", icon: "🗂️", title: "Choose a Category", desc: "Pick from 10 school categories like Secondary, Model, Sanskrit or Vocational.", c: "#6366f1" },
  { n: "02", icon: "📍", title: "Select Location", desc: "Filter by District and Block, or simply enter a 6-digit Pincode.", c: "#ec4899" },
  { n: "03", icon: "✅", title: "Get School Details", desc: "Verify the captcha, search, and view complete school information instantly.", c: "#14b8a6" },
];

const FEATURES = [
  { icon: "⚡", title: "Lightning Fast", desc: "Find any school in just a few clicks.", c: "#f59e0b" },
  { icon: "🌐", title: "English & हिंदी", desc: "Switch language anytime from the header.", c: "#0ea5e9" },
  { icon: "🌙", title: "Dark Mode", desc: "Easy on the eyes, day or night.", c: "#8b5cf6" },
  { icon: "📱", title: "Mobile Friendly", desc: "Works smoothly on every screen size.", c: "#22c55e" },
];

const QUICK_LINKS = [
  { to: "divoptions_ele_sec", icon: "🏫", label: "Elementary / Secondary" },
  // { to: "divoptions_Stream", icon: "🔬", label: "Stream Wise" },
  { to: "divoptions_vocationaltrade", icon: "🛠️", label: "Vocational" },
  { to: "divoptions_div_model", icon: "⭐", label: "Model Schools" },
  { to: "divoptions_cl", icon: "💻", label: "Computer Lab" },
];

/* =========================================================
   SVG ICONS
========================================================= */
const Icons = {
  school: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M32 4L4 18v4h56v-4L32 4zm-20 20v24h-4v4h48v-4h-4V24h-6v24h-6V24h-6v24h-6V24h-6v24h-6V24h-6z" /></svg>),
  search: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M28 8a20 20 0 100 40 20 20 0 000-40zm0 6a14 14 0 110 28 14 14 0 010-28zm16 30l10 10-4 4-10-10 4-4z" /></svg>),
  trade: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M20 8h24l4 10H16l4-10zm-4 14h32v30H16V22zm6 6v18h4V28h-4zm12 0v18h4V28h-4z" /></svg>),
  books: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M8 12h20v40H8V12zm2 2v36h16V14H10zm24-2h22v40H34V12zm2 2v36h18V14H36zM10 16h12v4H10v-4zm0 8h12v4H10v-4zm26-8h16v4H36v-4zm0 8h16v4H36v-4z" /></svg>),
  model: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M4 56V32l12-8 12 8v24H4zm2-2h8V40H6v14zm10 0h8V40h-8v14zM6 34h8v4H6v-4zm10 0h8v4h-8v-4zm24-16l14-10 14 10v38H40V18zm2 2v34h10V24h4v34h10V20L52 12 42 20z" /></svg>),
  mgs: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M32 4l-8 12h-8v40h32V16h-8L32 4zm-4 36v-8h8v8h-8zm-6-24h20v6H22v-6z" /></svg>),
  adarsh: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M32 6l-6 8-10-2 2 10-6 8 6 8-2 10 10-2 6 8 6-8 10 2-2-10 6-8-6-8 2-10-10 2-6-8zm0 12a14 14 0 110 28 14 14 0 010-28zm0 6a8 8 0 100 16 8 8 0 000-16z" /></svg>),
  sanskrit: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M8 12h48v40H8V12zm2 2v36h44V14H10zM14 20h6v4h-6v-4zm10 0h20v4H24v-4zM14 28h36v4H14v-4zm0 8h36v4H14v-4zm0 8h24v4H14v-4z" /></svg>),
  hostel: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M8 28L32 8l24 20v28H8V28zm2 2v24h44V30L32 12 10 30zm10 4h8v8h-8v-8zm16 0h8v8h-8v-8zm-16 12h8v8h-8v-8zm16 0h8v8h-8v-8z" /></svg>),
  computer: (<svg viewBox="0 0 64 64" fill="currentColor"><path d="M4 12h56v32H4V12zm2 2v28h52V14H6zm8 34h36v4H14v-4zm10 4h16l-2 6H26l-2-6z" /></svg>),
  refresh: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-3-6.7" /><polyline points="21 3 21 9 15 9" /></svg>),
  arrowRight: (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" /></svg>),
  searchSmall: (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>),
  globe: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>),
  moon: (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 3a9 9 0 109 9 7 7 0 11-9-9z" /></svg>),
  sun: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>),
};

/* =========================================================
   ROCKET
========================================================= */
const STAR_POS = [
  [8, 14, 0], [18, 70, 1.2], [30, 24, 0.6], [44, 82, 1.8], [52, 12, 0.3],
  [63, 58, 1.5], [72, 20, 0.9], [81, 76, 2.1], [90, 34, 0.4], [95, 12, 1.1],
  [12, 44, 1.7], [38, 50, 0.8],
];

const RocketFlight = () => (
  <div className="ssd-rocket-scene" aria-hidden="true">
    {STAR_POS.map(([l, tp, d], i) => (
      <span key={i} className="ssd-star" style={{ left: `${l}%`, top: `${tp}%`, animationDelay: `${d}s` }} />
    ))}
    <span className="ssd-planet" />
    <div className="ssd-rocket-path">
      <div className="ssd-rocket">
        <span className="ssd-rocket-trail" />
        <svg viewBox="0 0 64 110" xmlns="http://www.w3.org/2000/svg">
          <g className="ssd-rocket-flame">
            <path d="M32 78c-9 8-10 20-4 30 2-5 4-6 4-6s2 1 4 6c6-10 5-22-4-30z" fill="#ff7a1a" />
            <path d="M32 82c-5 6-6 13-2 20 1-3 2-4 2-4s1 1 2 4c4-7 3-14-2-20z" fill="#ffd24a" />
          </g>
          <path d="M18 52L4 74l4 6 14-8z" fill="#f4a300" />
          <path d="M46 52l14 22-4 6-14-8z" fill="#f4a300" />
          <path d="M32 2c-10 10-15 24-15 40v32h30V42C47 26 42 12 32 2z" fill="#ffffff" />
          <path d="M32 2c-10 10-15 24-15 40h8c0-14 3-28 7-40z" fill="#dfe6fa" />
          <path d="M32 2c-4 4-7 9-9 14h18c-2-5-5-10-9-14z" fill="#ef4444" />
          <circle cx="32" cy="40" r="9" fill="#1f3fbf" stroke="#0e1a3a" strokeWidth="3" />
          <circle cx="29" cy="37" r="3" fill="#7ea2ff" />
          <rect x="17" y="60" width="30" height="6" fill="#1f3fbf" />
          <rect x="22" y="74" width="20" height="6" rx="3" fill="#0e1a3a" />
        </svg>
      </div>
    </div>
    <span className="ssd-float ssd-float-1">📚</span>
    <span className="ssd-float ssd-float-2">✏️</span>
    <span className="ssd-float ssd-float-3">🎓</span>
    <span className="ssd-float ssd-float-4">⭐</span>
    <span className="ssd-float ssd-float-5">🔬</span>
  </div>
);

/* =========================================================
   SEARCH CATEGORY CARDS
========================================================= */
const CARDS = [
  { id: "div_ele_sec", to: "divoptions_ele_sec", icon: Icons.school, label: "Elementary / Secondary Schools", color: "#2563eb" },
  { id: "div_stream", to: "divoptions_Stream", icon: Icons.search, label: "Stream / Subject Wise Schools", color: "#7c3aed" },
  { id: "div_VocationalTrade", to: "divoptions_vocationaltrade", icon: Icons.trade, label: "Vocational Trade in Schools", color: "#f59e0b" },
  // { id: "div_peeo", to: "divoptions_div_peeo", icon: Icons.books, label: "PEEO Schools", color: "#10b981" },
  { id: "div_Modal", to: "divoptions_div_model", icon: Icons.model, label: "Model Schools", color: "#ef4444" },
  { id: "div_MGS", to: "divoptions_div_MGS", icon: Icons.mgs, label: "Mahatma Gandhi English Medium Schools", color: "#06b6d4" },
  // { id: "div_AU", to: "divoptions_AU", icon: Icons.adarsh, label: "Adarsh / Utkristh Scheme Schools", color: "#84cc16" },
  { id: "div_Sanskrit", to: "divoptions_Sanskrit", icon: Icons.sanskrit, label: "Sanskrit Schools", color: "#f97316" },
  { id: "div_HR", to: "divoptions_HR", icon: Icons.hostel, label: "Hostel / Residential Schools", color: "#6366f1" },
  { id: "div_cl", to: "divoptions_cl", icon: Icons.computer, label: "Schools with Computer Lab", color: "#14b8a6" },
];

/* =========================================================
   SEARCH PANEL CONFIG
   Pehle 10 baar copy-paste hua code tha — ab sab yahin se chalta hai.
   radio: "dist"  → "By District / Block" | "By Pincode"
          "all"   → "All Schools"         | "By Pincode"
   location: false → "All Schools" me district/block nahi dikhana (Model)
   captcha: { input, img, n }  | postback: button ka WebForms id
========================================================= */
const SECTIONS = [
  {
    key: "ele_sec", id: "divoptions_ele_sec", title: "Elementary / Secondary Schools",
    radio: "dist", dist: "ddlDistrict", block: "ddlBlock", pin: "txtPincode_ele_sec",
    extras: [{ name: "ddlSchoolType_dis", options: [["1", "Secondary"], ["2", "Elementary"]] }],
    captcha: { input: "txt_Captchaname1", img: "Img_elesec", n: 1 },
    postback: "btnSchoolSearch", group: "SchoolSearch",
  },
  {
    key: "Stream", id: "divoptions_Stream", title: "Stream / Subject Wise Schools",
    radio: "dist", dist: "ddlDistrict_Stream", block: "ddlBlock_Stream", pin: "txtPincode_Stream",
    extras: [
      {
        name: "ddlStream",
        options: [
          ["0", "-- Select Stream --"], ["1", "Science"], ["2", "Commerce"], ["3", "Arts"],
          ["4", "Agriculture (Science)"], ["5", "Vocational"], ["6", "Third Language"],
          ["7", "Optional"], ["8", "Sanskrit"],
        ],
      },
      { name: "ddlsubject", options: [["0", "-- Select Subject --"]] },
    ],
    captcha: { input: "txt_Captch_Stream_Dist", img: "Img_Stream", n: 7 },
    postback: "Button5", group: "SchoolSearch_Stream",
  },
  {
    key: "Vocational", id: "divoptions_vocationaltrade", title: "Vocational Trade in School",
    radio: "dist", dist: "ddlDistrict_Vocational", block: "ddlBlock_Vocational", pin: "txtPincode_Vocational",
    extras: [
      {
        name: "ddlvocationaltrade_dis",
        options: [
          ["0", "-- Select Vocational Trade --"], ["90", "Automobile / Automotive"],
          ["91", "Healthcare"], ["92", "IT & ITes"], ["93", "Beauty & Wellness"],
          ["94", "Security"], ["95", "Travel & Tourism / Tourism & Hospitality"],
          ["96", "Retail"], ["101", "Electrical & Electronics / Electronics & Hardware"],
          ["102", "Micro Irrigation / Agriculture"], ["103", "Apparel Made-up & Home Furnishing"],
          ["172", "Plumber"], ["173", "Telecom"],
        ],
      },
    ],
    captcha: { input: "txt_Captchavt_dist", img: "Img_vt_dist", n: 5 },
    postback: "Button3", group: "SchoolSearch_Vocational",
  },
  {
    key: "Peeo", id: "divoptions_div_peeo", title: "PEEO Schools",
    radio: "dist", dist: "ddlDistrict_peeo", block: "ddlBlock_peeo", pin: "txtPincode_peeo",
    captcha: { input: "txt_Captcha_dist_peeo", img: "Img_peeo1", n: 4 },
    postback: "Button1",
  },
  {
    key: "Modal", id: "divoptions_div_model", title: "Model Schools",
    radio: "all", location: false, pin: "txtPincode_Modal",
    infoTitle: "Model School Search",
    infoText: "Explore schools available under the Model School category.",
    popup: 9, postback: "Button7",
  },
  {
    key: "MGS", id: "divoptions_div_MGS", title: "Mahatma Gandhi English Medium Schools",
    radio: "all", dist: "ddldistrict_MGS", block: "ddlblock_MGS", pin: "txtPincode_MGS",
    popup: 11, postback: "Button9",
  },
  {
    key: "AU", id: "divoptions_AU", title: "Under Adarsh / Utkristh Scheme Schools",
    radio: "dist", dist: "ddlDistrict_AU", block: "ddlBlock_AU", pin: "txtPincode_AU",
    extras: [{ name: "ddlSchoolType_AU", options: [["1", "Under Adarsh Scheme"], ["2", "Utkristh"]] }],
    captcha: { input: "txt_Captcha_au_dist", img: "Img_au_dist", n: 13 },
    postback: "Button11", group: "SchoolSearch_AU",
  },
  {
    key: "Sanskrit", id: "divoptions_Sanskrit", title: "Sanskrit Schools",
    radio: "dist", dist: "ddlDistrict_Sanskrit", block: "ddlBlock_Sanskrit", pin: "txtPincode_Sanskrit",
    captcha: { input: "txt_Captcha_sans_dist", img: "Img_sanskrit_dist", n: 15 },
    postback: "Button13",
  },
  {
    key: "HR", id: "divoptions_HR", title: "Hostel / Residential Schools",
    radio: "dist", dist: "ddlDistrict_HR", block: "ddlBlock_HR", pin: "txtPincode_HR",
    extras: [{ name: "ddlSchoolType_HR", options: [["1", "Residential"], ["2", "Hostel"]] }],
    captcha: { input: "txt_Captcha_hss_dist", img: "Img_hss_dist", n: 17 },
    postback: "Button15", group: "SchoolSearch_HR",
  },
  {
    key: "cl", id: "divoptions_cl", title: "Schools with Computer Lab",
    radio: "dist", dist: "ddlDistrict_cl", block: "ddlBlock_cl", pin: "txtPincode_cl",
    captcha: { input: "txt_Captcha_cl_dist", img: "Img_cl_dist", n: 19 },
    postback: "Button17",
  },
];

const RADIO_OPTIONS = {
  dist: [
    { value: 1, label: "By District / Block" },
    { value: 4, label: "By Pincode" },
  ],
  all: [
    { value: 1, label: "All Schools" },
    { value: 4, label: "By Pincode" },
  ],
};

/* =========================================================
   SMALL COMPONENTS
========================================================= */
const makeCaptcha = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 6; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const RadioFilter = ({ name, options, value, onChange }) => (
  <div className="radioBtns">
    <span className={`rbtfilteroptions_${name}`}>
      {options.map((option, index) => (
        <React.Fragment key={option.value}>
          <input
            id={`rbtfilteroptions_${name}_${index}`}
            type="radio"
            name={`rbtfilteroptions_${name}`}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <label htmlFor={`rbtfilteroptions_${name}_${index}`}>
            <span className="radio-dot" />
            {option.label}
          </label>
        </React.Fragment>
      ))}
    </span>
  </div>
);

const SelectBox = ({ name, id, value, onChange, disabled, defaultValue, children }) => (
  <div className="select-wrapper">
    <select
      name={name}
      id={id}
      className="form-control"
      {...(value !== undefined ? { value, onChange } : { defaultValue })}
      disabled={disabled}
    >
      {children}
    </select>
  </div>
);

/* Captcha ab sach me check hota hai (client-side) — value parent me rehti hai */
const CaptchaField = ({ cfg, text, value, onChange, onRefresh }) => (
  <div className="captcha-row">
    <div className="captcha-input-wrapper">
      <div className="field-label">
        <span>SECURITY VERIFICATION</span>
      </div>
      <input
        name={cfg.input}
        type="text"
        autoComplete="off"
        maxLength={6}
        className={`${cfg.input} form-control search_text_feild`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter Captcha (Case Sensitive)"
        aria-label="Enter captcha"
      />
    </div>
    <div className="captcha-display-wrapper">
      <div className="captcha-box">
        <div
          className={`captcha-placeholder ${cfg.img}`}
          onClick={onRefresh}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && onRefresh()}
          title="Click to refresh captcha"
          aria-label={`Captcha ${text.split("").join(" ")}`}
        >
          <span className="captcha-noise noise-one" />
          <span className="captcha-noise noise-two" />
          <span className="captcha-text">{text}</span>
        </div>
        <button type="button" className="refresh-icon" onClick={onRefresh} aria-label="Refresh captcha">
          {Icons.refresh}
        </button>
      </div>
    </div>
  </div>
);

/* ---------- ek hi component se saare 10 search panels ---------- */
const SearchPanel = ({ cfg, show }) => {
  const [radio, setRadio] = useState(1);
  const [district, setDistrict] = useState("");
  const [block, setBlock] = useState("");
  const [pincode, setPincode] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");

  const regenerate = useCallback(() => {
    setCaptchaText(makeCaptcha());
    setCaptchaValue("");
  }, []);

  useEffect(() => {
    if (cfg.captcha) regenerate();
  }, [cfg.captcha, regenerate]);

  const hasLocation = cfg.location !== false && cfg.dist;
  const byPincode = radio === 4;
  const blocks = useMemo(
    () => (district ? BLOCKS_BY_DISTRICT[district] || [] : []),
    [district]
  );

  const submit = () => {
    if (!byPincode && cfg.radio === "dist" && !district) {
      emitToast("Please select a district.", "error");
      return;
    }
    if (byPincode && !/^\d{6}$/.test(pincode)) {
      emitToast("Please enter a valid 6-digit pincode.", "error");
      return;
    }
    if (cfg.captcha) {
      if (captchaValue !== captchaText) {
        emitToast("Captcha does not match. Please try again.", "error");
        regenerate();
        return;
      }
      ValidateCaptcha(cfg.captcha.img, cfg.captcha.input, cfg.captcha.n);
    }
    if (cfg.popup) showpopup(cfg.popup);
    WebForm_DoPostBackWithOptions(
      WebForm_PostBackOptions(P(cfg.postback), "", true, cfg.group || "", "", false, false)
    );
    emitToast(`Searching ${cfg.title}…`, "success");
  };

  return (
    <div className={`containerlst ${show ? "active" : ""}`} id={cfg.id}>
      <div className="searchDiv" onMouseMove={handleSpotlight}>
        <div className="section-glow glow-one" />
        <div className="section-glow glow-two" />

        <div className="card-bg">
          <div className="section-header">
            <span className="section-header-kicker">SCHOOL SEARCH</span>
            <h2>{cfg.title}</h2>
            <p>Select your preferred search options to continue.</p>
          </div>

          <RadioFilter
            name={cfg.key}
            options={RADIO_OPTIONS[cfg.radio]}
            value={radio}
            onChange={setRadio}
          />

          {byPincode ? (
            <div className="selctBox">
              <div className="select-wrapper">
                <input
                  type="text"
                  inputMode="numeric"
                  name={P(cfg.pin)}
                  id={cfg.pin}
                  className="form-control"
                  placeholder="Enter 6-digit Pincode"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submit())}
                />
              </div>
            </div>
          ) : hasLocation ? (
            <div className="selctBox">
              <SelectBox
                name={P(cfg.dist)}
                id={cfg.dist}
                value={district}
                onChange={(e) => {
                  setDistrict(e.target.value);
                  setBlock("");
                  setTimeout(() => doPostBack(P(cfg.dist), ""), 0);
                }}
              >
                <option value="">Select District Name</option>
                {DISTRICTS.map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </SelectBox>

              <SelectBox
                name={P(cfg.block)}
                id={cfg.block}
                value={block}
                onChange={(e) => setBlock(e.target.value)}
                disabled={!district}
              >
                <option value="">
                  {district
                    ? blocks.length ? "Select Block Name" : "No blocks available"
                    : "Select District First"}
                </option>
                {blocks.map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </SelectBox>

              {(cfg.extras || []).map((ex) => (
                <SelectBox
                  key={ex.name}
                  name={P(ex.name)}
                  id={ex.name}
                  defaultValue={ex.options[0][0]}
                >
                  {ex.options.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </SelectBox>
              ))}
            </div>
          ) : (
            <div className="premium-info-box">
              <div className="premium-info-icon">★</div>
              <div>
                <strong>{cfg.infoTitle}</strong>
                <p>{cfg.infoText}</p>
              </div>
            </div>
          )}

          {cfg.captcha && (
            <CaptchaField
              cfg={cfg.captcha}
              text={captchaText}
              value={captchaValue}
              onChange={setCaptchaValue}
              onRefresh={regenerate}
            />
          )}

          <div className="search-btn-wrapper">
            <button
              type="button"
              className="btn searchDivBtn"
              onMouseMove={handleMagnetic}
              onMouseLeave={resetMagnetic}
              onClick={withRipple(submit)}
            >
              <span>Search Schools</span>
              <span className="btn-arrow">{Icons.arrowRight}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnimatedCounter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const run = () => {
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / 1800, 1);
        setCount(Math.floor(value * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
        else setCount(value);
      };
      raf = requestAnimationFrame(tick);
    };
    if (!("IntersectionObserver" in window)) {
      setCount(value);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          run();
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value]);

  return (
    <span ref={ref}>
      {count.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
};

const FaqItem = ({ q, a, open, onToggle, index }) => (
  <div className={`faq-item ${open ? "open" : ""}`}>
    <button
      type="button"
      className="faq-question"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={`faq-panel-${index}`}
    >
      <span>{q}</span>
      <span className="faq-icon" aria-hidden="true">{open ? "−" : "+"}</span>
    </button>
    <div className="faq-answer" id={`faq-panel-${index}`} role="region">
      <p>{a}</p>
    </div>
  </div>
);

const SectionHeading = ({ kicker, title, sub }) => {
  const [lead, last] = splitTitle(title);
  return (
    <div className="ssd-section-heading">
      <div className="premium-kicker">
        <span className="kicker-line" />
        {kicker}
        <span className="kicker-line" />
      </div>
      <h2 className="ssd-section-title">
        {lead} <span>{last}</span>
      </h2>
      {sub && <p className="ssd-section-sub">{sub}</p>}
    </div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */
function MyClass() {
  const [activeSection, setActiveSection] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [imgErrors, setImgErrors] = useState({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showTopButton, setShowTopButton] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState("");

  /* Theme: saved value pehle hi read hoti hai (overwrite bug fix) */
  const [darkMode, setDarkMode] = useState(() => store.get("ssd-dark") === "1");
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const saved = JSON.parse(store.get("ssd-recent", "[]"));
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  });

  const { lang, t, change: changeLang } = useLang();
  const toasts = useToasts();
  const wrapRef = useRef(null);

  /* ---------- theme save ---------- */
  useEffect(() => {
    store.set("ssd-dark", darkMode ? "1" : "0");
  }, [darkMode]);

  /* ---------- hero autoplay (hover par ruk jata hai) ---------- */
  useEffect(() => {
    if (paused) return;
    const id = setInterval(
      () => setCurrentSlide((p) => (p + 1) % sliders.length),
      6000
    );
    return () => clearInterval(id);
  }, [paused, currentSlide]);

  /* ---------- scroll: header, back-to-top, progress bar ---------- */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setShowTopButton(y > 500);
      setScrolled(y > 60);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      wrapRef.current?.style.setProperty(
        "--ssd-scroll-progress",
        `${max > 0 ? (y / max) * 100 : 0}%`
      );
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ---------- testimonials auto-rotate ---------- */
  useEffect(() => {
    const id = setInterval(
      () => setActiveTestimonial((p) => (p + 1) % TESTIMONIALS.length),
      6000
    );
    return () => clearInterval(id);
  }, [activeTestimonial]);

  /* ---------- reveal on scroll ---------- */
  useEffect(() => {
    const els = document.querySelectorAll(".app-wrapper .ssd-reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("in"));
      return;
    }
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("in");
            obs.unobserve(en.target);
          }
        }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  /* ---------- mobile nav: Esc se band + body scroll lock ---------- */
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => e.key === "Escape" && setMobileNavOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  /* ---------- category card toggle ---------- */
  const toggleSection = useCallback((id) => {
    setActiveSection((prev) => (prev === id ? null : id));
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 150);
  }, []);

  const nextSlide = () => setCurrentSlide((p) => (p + 1) % sliders.length);
  const prevSlide = () =>
    setCurrentSlide((p) => (p - 1 + sliders.length) % sliders.length);

  /* ---------- global search ---------- */
  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) {
      emitToast("Please enter a valid school code.", "error");
      return;
    }
    setRecentSearches((prev) => {
      const next = [q, ...prev.filter((x) => x !== q)].slice(0, 5);
      store.set("ssd-recent", JSON.stringify(next));
      return next;
    });
    emitToast(`Searching for school code: ${q}`, "success");
    showpopup(25);
    WebForm_DoPostBackWithOptions(
      WebForm_PostBackOptions(P("Button23"), "", true, "", "", false, false)
    );
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(newsletterEmail)) {
      emitToast("Please enter a valid email address.", "error");
      return;
    }
    emitToast("Subscribed successfully! 🎉", "success");
    setNewsletterEmail("");
  };

  const closeNav = () => setMobileNavOpen(false);

  const NAV = [
    { href: "#top", label: t.home },
    { href: "#school-search-area", label: t.searchSchools },
    { href: "#school-categories-area", label: "Categories" },
    { href: "#schemes-area", label: t.schemes },
    { href: "#faq-area", label: "FAQ" },
  ];

  return (
    <div
      ref={wrapRef}
      id="top"
      data-ssd-theme={darkMode ? "dark" : "light"}
      className={`app-wrapper ${mobileNavOpen ? "mobile-nav-active" : ""} ${darkMode ? "dark" : ""}`}
    >
      <div className="ssd-scroll-progress" />

      {/* ================= HEADER ================= */}

      {/* ================= HERO ================= */}
      <section
        className="hero_banner"
        onMouseMove={handleSpotlight}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="hero-grid" />
        <div className="hero-orb hero-orb-one" />
        <div className="hero-orb hero-orb-two" />
        <div className="hero-orb hero-orb-three" />

        {sliders.map((slide, index) => (
          <div
            key={slide.eyebrow}
            className={`hero_slide ${currentSlide === index ? "active" : ""}`}
            aria-hidden={currentSlide !== index}
          >
            {slide.src && !imgErrors[index] ? (
              <img
                src={slide.src}
                alt=""
                className="hero_img"
                onError={() => setImgErrors((p) => ({ ...p, [index]: true }))}
              />
            ) : (
              <div className="hero_fallback">
                <span>{slide.fallback}</span>
              </div>
            )}

            <div className="hero-overlay" />

            <figure className="hero-content">
              <figcaption>
                <div className="hero-eyebrow">
                  <span className="hero-eyebrow-dot" />
                  {slide.eyebrow}
                </div>

                <h1 className="heading-1">
                  {slide.title}
                  <br />
                  <span>{slide.highlight}</span>
                </h1>

                <p className="hero-description">{slide.subtitle}</p>

                <div className="hero-actions">
                  <button
                    type="button"
                    className="hero-primary-btn"
                    onMouseMove={handleMagnetic}
                    onMouseLeave={resetMagnetic}
                    onClick={withRipple(() => scrollToId("school-search-area"))}
                  >
                    {t.findSchool}
                    <span>{Icons.arrowRight}</span>
                  </button>
                  <button
                    type="button"
                    className="hero-secondary-btn"
                    onMouseMove={handleMagnetic}
                    onMouseLeave={resetMagnetic}
                    onClick={withRipple(() => scrollToId("school-categories-area"))}
                  >
                    {t.explore}
                  </button>
                </div>
              </figcaption>
            </figure>
          </div>
        ))}

        <button className="slider-arrow slider-arrow-prev" onClick={prevSlide} aria-label="Previous slide">‹</button>
        <button className="slider-arrow slider-arrow-next" onClick={nextSlide} aria-label="Next slide">›</button>

        <div className="slider_dots">
          {sliders.map((_, index) => (
            <button
              key={index}
              type="button"
              className={currentSlide === index ? "active" : ""}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <RocketFlight />
        <div className="hero-bottom-fade" />
      </section>

      {/* ================= NOTICE MARQUEE ================= */}
      <section className="ssd-marquee-bar" aria-label={t.notices}>
        <div className="ssd-marquee-label">
          <span className="ssd-live-dot" />
          {t.notices}
        </div>
        <div className="ssd-marquee-track">
          <div className="ssd-marquee-content">
            {[...NOTICES, ...NOTICES].map((n, i) => (
              <span key={i} className="ssd-marquee-item" aria-hidden={i >= NOTICES.length}>
                <strong>{n.date}</strong> {n.text}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="ssd-stats-section ssd-reveal">
        <div className="container">
          <SectionHeading kicker="PLATFORM OVERVIEW" title={t.stats} />
          <div className="ssd-stats-grid">
            {STATS.map((s) => (
              <div key={s.label} className="ssd-stat-card" style={{ "--c": s.c }}>
                <span className="ssd-stat-icon">{s.icon}</span>
                <h3 className="ssd-stat-value">
                  <AnimatedCounter value={s.value} suffix={s.suffix} />
                </h3>
                <p className="ssd-stat-label">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="ssd-how-section ssd-reveal">
        <div className="container">
          <SectionHeading kicker="HOW IT WORKS" title="Find a School in 3 Easy Steps" />

          <div className="ssd-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="ssd-step" style={{ "--c": s.c }}>
                <span className="ssd-step-num">{s.n}</span>
                <span className="ssd-step-icon">{s.icon}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="ssd-features">
            {FEATURES.map((f) => (
              <div key={f.title} className="ssd-feature" style={{ "--c": f.c }}>
                <span className="ssd-feature-icon">{f.icon}</span>
                <div>
                  <strong>{f.title}</strong>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SEARCH AREA ================= */}
      <section className="main-content" id="school-search-area">
        <div className="container">
          <form onSubmit={(e) => e.preventDefault()} id="form1" noValidate>
            <div className="premium-heading-block search-heading-block">
              <div className="heading-background-grid" />
              <span className="heading-orb heading-orb-one" />
              <span className="heading-orb heading-orb-two" />
              <div className="heading-content">
                <div className="premium-kicker">
                  <span className="kicker-line" />
                  SMART SCHOOL SEARCH
                  <span className="kicker-line" />
                </div>
                <h2>
                  Find Your <span>School</span>
                </h2>
                <p>{t.searchPlaceholder}</p>
              </div>
            </div>

            {/* global search bar */}
            <div className="search-section-wrapper">
              <div className={`search_school_bar ${isSearchFocused ? "focused" : ""}`}>
                <div className="search-icon-wrapper">{Icons.searchSmall}</div>
                <input
                  id="searchBar"
                  className="searchbar"
                  type="text"
                  placeholder={t.searchPlaceholder}
                  aria-label={t.searchPlaceholder}
                  value={searchQuery}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <button
                  type="button"
                  name={P("Button23")}
                  id="ContentPlaceHolder1_Button23"
                  className="btn-search"
                  onClick={withRipple(handleSearch)}
                  onMouseMove={handleMagnetic}
                  onMouseLeave={resetMagnetic}
                >
                  <span>{t.search}</span>
                  <span className="search-btn-arrow">→</span>
                </button>
              </div>

              <div className="ssd-quick-chips">
                <span className="ssd-chip-label">{t.quickAccess}</span>
                {QUICK_LINKS.map((q) => (
                  <button key={q.to} type="button" className="ssd-chip" onClick={() => toggleSection(q.to)}>
                    <span>{q.icon}</span> {q.label}
                  </button>
                ))}
              </div>

              {recentSearches.length > 0 && (
                <div className="ssd-quick-chips">
                  <span className="ssd-chip-label">{t.recent}</span>
                  {recentSearches.map((r) => (
                    <button key={r} type="button" className="ssd-chip" onClick={() => setSearchQuery(r)}>
                      🕘 {r}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="ssd-chip ssd-chip-clear"
                    onClick={() => {
                      setRecentSearches([]);
                      store.remove("ssd-recent");
                    }}
                  >
                    {t.clear}
                  </button>
                </div>
              )}
            </div>

            {/* categories heading */}
            <div className="premium-heading-block categories-heading-block" id="school-categories-area">
              <div className="heading-background-grid" />
              <span className="heading-orb heading-orb-one" />
              <span className="heading-orb heading-orb-two" />
              <div className="heading-content">
                <div className="premium-kicker">
                  <span className="kicker-line" />
                  EXPLORE SERVICES
                  <span className="kicker-line" />
                </div>
                <h2>
                  School Search <span>Categories</span>
                </h2>
                <p>{t.categoriesSub}</p>
              </div>
            </div>

            {/* category cards */}
            <div className="card-container" id="divsearchdashboard">
              {CARDS.map((card, i) => (
                <div
                  key={card.id}
                  id={card.id}
                  className={`item ${activeSection === card.to ? "active" : ""}`}
                  onClick={() => toggleSection(card.to)}
                  onMouseMove={handleCardTilt}
                  onMouseLeave={resetCardTilt}
                  role="button"
                  tabIndex={0}
                  aria-pressed={activeSection === card.to}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection(card.to);
                    }
                  }}
                  style={{ "--card-color": card.color }}
                >
                  <span className="card-number">{String(i + 1).padStart(2, "0")}</span>
                  <span className="card-shine" />

                  <div className="firtsDiv">
                    <figure className="card-icon">{card.icon}</figure>
                    <figcaption>
                      <h4>{card.label}</h4>
                    </figcaption>
                    <div className="card-mini-arrow">→</div>
                  </div>

                  <div className="secondDiv">
                    <div className="active-card-icon">{card.icon}</div>
                    <div className="active-card-content">
                      <span>SCHOOL CATEGORY</span>
                      <strong>{card.label}</strong>
                    </div>
                    <span className="card-arrow">{Icons.arrowRight}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* search panels */}
            <div className="sections-wrapper">
              {SECTIONS.map((cfg) => (
                <SearchPanel key={cfg.key} cfg={cfg} show={activeSection === cfg.id} />
              ))}
            </div>
          </form>
        </div>
      </section>

      {/* ================= SCHEMES ================= */}
      <section className="ssd-schemes-section ssd-reveal" id="schemes-area">
        <div className="container">
          <SectionHeading
            kicker="GOVERNMENT INITIATIVES"
            title={t.schemes}
            sub="Discover government schemes and programs supporting education across Rajasthan."
          />
          <div className="ssd-schemes-grid">
            {SCHEMES.map((s) => (
              <div
                key={s.title}
                className="ssd-scheme-card"
                style={{ "--scheme-color": s.color }}
                onMouseMove={handleSpotlight}
              >
                <div className="ssd-scheme-top">
                  <span className="ssd-scheme-icon">{s.icon}</span>
                  <span className="ssd-scheme-tag">{s.tag}</span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <button type="button" className="ssd-scheme-link">
                  Learn More {Icons.arrowRight}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="ssd-faq-section ssd-reveal" id="faq-area">
        <div className="container">
          <SectionHeading
            kicker="HELP CENTER"
            title={t.faq}
            sub="Quick answers to common questions about the platform."
          />
          <div className="ssd-faq-list">
            {FAQS.map((f, i) => (
              <FaqItem
                key={f.q}
                index={i}
                q={f.q}
                a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="ssd-testimonials-section ssd-reveal">
        <div className="container">
          <SectionHeading kicker="TESTIMONIALS" title={t.testimonials} />

          <div className="ssd-testimonials-wrap">
            <div className="ssd-testimonial-card" key={activeTestimonial} aria-live="polite">
              <div className="ssd-testimonial-quote" aria-hidden="true">“</div>
              <p className="ssd-testimonial-text">{TESTIMONIALS[activeTestimonial].text}</p>
              <div className="ssd-testimonial-author">
                <span className="ssd-testimonial-avatar">{TESTIMONIALS[activeTestimonial].avatar}</span>
                <div>
                  <strong>{TESTIMONIALS[activeTestimonial].name}</strong>
                  <small>{TESTIMONIALS[activeTestimonial].role}</small>
                </div>
              </div>
            </div>

            <div className="ssd-testimonial-dots">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={activeTestimonial === i ? "active" : ""}
                  onClick={() => setActiveTestimonial(i)}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="ssd-cta-section ssd-reveal">
        <div className="container">
          <div className="ssd-cta-box">
            <span className="ssd-cta-orb" />
            <div className="ssd-cta-text">
              <h2>{t.ctaTitle}</h2>
              <p>
                Search across {CARDS.length} categories, {DISTRICTS.length} districts and
                thousands of schools in Rajasthan, completely free.
              </p>
            </div>
            <div className="ssd-cta-actions">
              <button type="button" className="hero-primary-btn" onClick={withRipple(() => scrollToId("school-search-area"))}>
                {t.findSchool}
                <span>{Icons.arrowRight}</span>
              </button>
              <button type="button" className="hero-secondary-btn" onClick={withRipple(() => scrollToId("faq-area"))}>
                {t.readFaq}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
  

      {/* ================= MOBILE NAV ================= */}
      <nav className="mobile-nav" aria-label="Mobile navigation" aria-hidden={!mobileNavOpen}>
        <div className="mobile-nav-brand">
          <span className="brand-symbol">S</span>
          <div>
            <strong>{t.brand}</strong>
            <small>Darpan</small>
          </div>
          <button type="button" onClick={closeNav} aria-label="Close menu">×</button>
        </div>
        <ul>
          {NAV.map((n, i) => (
            <li key={n.href} className={i === 0 ? "active" : ""}>
              <a href={n.href} onClick={closeNav}>{n.label}</a>
            </li>
          ))}
        </ul>
      </nav>
      <div className="mobile-nav-overly" onClick={closeNav} />

      {/* ================= BACK TO TOP ================= */}
      <button
        type="button"
        className={`back-top ${showTopButton ? "show" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
      >
        ↑
      </button>

      {/* ================= TOASTS ================= */}
      <div className="ssd-toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`ssd-toast ssd-toast-${toast.type}`}>
            <span className="ssd-toast-icon">
              {toast.type === "success" ? "✓" : toast.type === "error" ? "!" : "i"}
            </span>
            <span className="ssd-toast-msg">{toast.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MyClass;